/**
 * Loan Modification Seed Data
 *
 * Contains sample loan modification requests for seeding via the
 * business.loanModifications.create tRPC endpoint.
 *
 * Covers multiple modification types: rate change, term extension,
 * forbearance, assumption, and partial payoff.
 *
 * DEPENDENCY: Requires cases with active loans to exist.
 *
 * @see Issue #617 - Loan Modification Workflows
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';

// =============================================================================
// SEED IDs
// =============================================================================

export const LOAN_MODIFICATION_IDS = {
  /** Rate reduction on a bridge loan */
  RATE_CHANGE_BRIDGE: seedId('loan-mod-rate', 1),
  /** Term extension on a construction loan */
  TERM_EXTENSION_CONSTRUCTION: seedId('loan-mod-term', 2),
  /** Forbearance for hardship borrower */
  FORBEARANCE_HARDSHIP: seedId('loan-mod-forbear', 3),
  /** Partial payoff reducing principal */
  PARTIAL_PAYOFF: seedId('loan-mod-payoff', 4),
} as const;

// =============================================================================
// DATA TYPES
// =============================================================================

export type LoanModificationSeedType =
  | 'RATE_CHANGE'
  | 'TERM_EXTENSION'
  | 'MATURITY_EXTENSION'
  | 'FORBEARANCE'
  | 'ASSUMPTION'
  | 'PARTIAL_PAYOFF';

export interface LoanModificationSeedData {
  id: string;
  businessId: string;
  /** Placeholder — resolved at seed time from existing cases */
  caseIndex: number;
  modificationType: LoanModificationSeedType;
  effectiveDate: string;
  beforeValues: Record<string, unknown>;
  afterValues: Record<string, unknown>;
  reason: string;
  notes?: string;
  requiredApprovals: number;
  /** Whether to also submit + approve after creation */
  autoSubmit: boolean;
  autoApprove: boolean;
}

// =============================================================================
// MODIFICATION DATA
// =============================================================================

export const LOAN_MODIFICATIONS: Record<keyof typeof LOAN_MODIFICATION_IDS, LoanModificationSeedData> = {
  RATE_CHANGE_BRIDGE: {
    id: LOAN_MODIFICATION_IDS.RATE_CHANGE_BRIDGE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseIndex: 0,
    modificationType: 'RATE_CHANGE',
    effectiveDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    beforeValues: {
      interestRate: 11.5,
      monthlyPayment: 4312.5,
    },
    afterValues: {
      interestRate: 10.25,
      monthlyPayment: 3843.75,
    },
    reason:
      'Borrower has successfully completed 3 prior projects with on-time payoffs. Rate reduction incentive for repeat borrower loyalty program.',
    notes:
      'Approved by senior loan officer per repeat borrower policy. Borrower has maintained excellent payment history across all three prior bridge loans.',
    requiredApprovals: 1,
    autoSubmit: true,
    autoApprove: true,
  },

  TERM_EXTENSION_CONSTRUCTION: {
    id: LOAN_MODIFICATION_IDS.TERM_EXTENSION_CONSTRUCTION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseIndex: 1,
    modificationType: 'TERM_EXTENSION',
    effectiveDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
    beforeValues: {
      maturityDate: '2026-06-15',
      termMonths: 12,
    },
    afterValues: {
      maturityDate: '2026-12-15',
      termMonths: 18,
      extensionFee: 5000,
    },
    reason:
      'Construction delayed due to permitting issues with county building department. Borrower has maintained all draw schedule requirements and construction is 65% complete.',
    notes: '6-month extension with $5,000 extension fee. County permit delay documented with official correspondence.',
    requiredApprovals: 2,
    autoSubmit: true,
    autoApprove: false,
  },

  FORBEARANCE_HARDSHIP: {
    id: LOAN_MODIFICATION_IDS.FORBEARANCE_HARDSHIP,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseIndex: 2,
    modificationType: 'FORBEARANCE',
    effectiveDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
    beforeValues: {
      monthlyPayment: 3750,
      paymentStatus: 'current',
    },
    afterValues: {
      monthlyPayment: 1500,
      forbearanceStartDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      forbearanceEndDate: new Date(Date.now() + 93 * 24 * 60 * 60 * 1000).toISOString(),
      forbearanceDurationMonths: 3,
    },
    reason:
      'Borrower experiencing temporary hardship due to property damage from recent storm. Insurance claim filed and pending. Reduced payment covers interest-only during forbearance period.',
    notes:
      'Hardship letter and insurance claim documentation on file. Property inspection scheduled for next week. Repayment plan: deferred balance added to maturity with no penalty.',
    requiredApprovals: 2,
    autoSubmit: true,
    autoApprove: false,
  },

  PARTIAL_PAYOFF: {
    id: LOAN_MODIFICATION_IDS.PARTIAL_PAYOFF,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    caseIndex: 0,
    modificationType: 'PARTIAL_PAYOFF',
    effectiveDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    beforeValues: {
      principalBalance: 850000,
      monthlyPayment: 7968.75,
    },
    afterValues: {
      principalBalance: 600000,
      payoffAmount: 250000,
      monthlyPayment: 5625,
    },
    reason:
      'Borrower sold one of two collateral properties and applying $250,000 from sale proceeds to reduce principal balance. Requesting corresponding payment reduction.',
    notes:
      'Closing statement from property sale attached. Title company confirmed wire of $250,000 to escrow. Recalculated amortization schedule to reflect reduced balance.',
    requiredApprovals: 1,
    autoSubmit: true,
    autoApprove: true,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllLoanModifications(): LoanModificationSeedData[] {
  return Object.values(LOAN_MODIFICATIONS);
}

export function getLoanModificationsByBusiness(businessId: string): LoanModificationSeedData[] {
  return Object.values(LOAN_MODIFICATIONS).filter((mod) => mod.businessId === businessId);
}

export function getLoanModificationsByType(type: LoanModificationSeedType): LoanModificationSeedData[] {
  return Object.values(LOAN_MODIFICATIONS).filter((mod) => mod.modificationType === type);
}
